import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { Subject, takeUntil } from 'rxjs';
import { EditOrderAdditionalServiceRequest } from '../../../../../models/orderModel';
import { MyConfigOrganization } from '../../../../../models/myConfig';

@Component({
  selector: 'app-back-office24-orders-edit-additional-service',
  standalone: false,
  templateUrl: './back-office24-orders-edit-additional-service.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './back-office24-orders-edit-additional-service.component.css'
})
export class BackOffice24OrdersEditAdditionalServiceComponent {
  servicesForm: any;
  componentDestroyed$: Subject<boolean> = new Subject();
  editAdditionalServiceForm: UntypedFormGroup;
  @Output() saveChangesEvent = new EventEmitter<void>();
  statusOptions: { value: string; name: string }[] = [
    { value: 'New', name: 'New' },
    { value: 'Paid', name: 'Paid' },
    { value: 'Planned', name: 'Planned' },
    { value: 'Underway', name: 'Underway' },
    { value: 'Completed', name: 'Completed' },
    { value: 'Cancelled', name: 'Cancelled' },
    { value: 'Removed', name: 'Removed' },
  ];
  additionalServiceStatusValues = {
    "New": 1,
    "Paid": 2,
    "Planned": 4,
    "Underway": 8,
    "Completed": 16,
    "Removed": 32,
    "Cancelled": 64
  }

  isLoading: boolean = false;
  availableTimeSlots: any[] = [];
  selectedDate: Date | null = null;
  selectedTimeSlot: any = null;
  showTimeSlotDropdown: boolean = false;
  availableDates: string[] = [];
  timeslots: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      additionalService: any,
      status: string,
      organization: MyConfigOrganization,
      parkingFrom: any,
      parkingTill: any,
      onlyCalendar: boolean,
      availableDates?: string[],
      timeslots?: any[],
      serviceTemplateId?: string,
      originalParkingFrom?: string,
      originalParkingTill?: string,
    },
    private fb: UntypedFormBuilder,
    public backofficeService: BackOfficeService,
    public snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<BackOffice24OrdersEditAdditionalServiceComponent>
  ) {
    this.initEmptyAdditionalServiceForm();
  }

  ngOnInit(): void {
    const originalParkingFrom = this.data.parkingFrom;
    const originalParkingTill = this.data.parkingTill;

    if (this.data.parkingFrom && this.data.parkingTill) {
      this.data.parkingFrom = new Date(this.data?.parkingFrom);
      this.data.parkingTill = new Date(this.data?.parkingTill);
      // Store originals for API calls
      this.data.originalParkingFrom = originalParkingFrom;
      this.data.originalParkingTill = originalParkingTill;
    }
    this.initEditAdditionalServiceForm();
    this.loadTimeslots();

    if (this.data.additionalService.planned) {
      this.selectedDate = new Date(this.data.additionalService.planned);
    }
  }

  private toSwedishISOString(date: Date): string {
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const partsMap: any = {};
    parts.forEach(part => {
      partsMap[part.type] = part.value;
    });

    const offset = this.getSwedishTimezoneOffset(date);

    return `${partsMap.year}-${partsMap.month}-${partsMap.day}T${partsMap.hour}:${partsMap.minute}:${partsMap.second}.00${offset}`;
  }

  private getSwedishTimezoneOffset(date: Date): string {
    const offsetMinutes = this.getSwedishOffsetMinutes(date);
    const offsetHours = Math.floor(offsetMinutes / 60);
    const offsetMins = offsetMinutes % 60;

    return `+${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`;
  }

  private getSwedishOffsetMinutes(date: Date): number {
    const year = date.getFullYear();
    const dstStart = new Date(year, 2, 31 - new Date(year, 2, 31).getDay());
    const dstEnd = new Date(year, 9, 31 - new Date(year, 9, 31).getDay());

    const isDST = date >= dstStart && date < dstEnd;
    return isDST ? 120 : 60;
  }

  private normalizeToSwedishFormat(dateString: string): string {
    const date = new Date(dateString);
    return this.toSwedishISOString(date);
  }

  async loadTimeslots(): Promise<void> {
    try {
      let fromDate: string;
      let toDate: string;
      const today = new Date();
      // Get Swedish time by using Intl.DateTimeFormat to get the actual Swedish time components
      const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Europe/Stockholm',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const parts = formatter.formatToParts(today);
      const partsMap: any = {};
      parts.forEach(part => {
        partsMap[part.type] = part.value;
      });

      if (this.data.additionalService.from && this.data.additionalService.to) {
        const fromDateObj = new Date(this.data.additionalService.from);
        const toDateObj = new Date(this.data.additionalService.to);
        const fromDateNormalized = this.data.additionalService.from.replace(/\.\d+/, '.00') + this.getSwedishTimezoneOffset(fromDateObj);
        const todaySwedish = this.toSwedishISOString(today);

        if (fromDateNormalized < todaySwedish) {
          fromDate = todaySwedish;
        } else {
          fromDate = fromDateNormalized;
        }
        toDate = this.data.additionalService.to.replace(/\.\d+/, '.00') + this.getSwedishTimezoneOffset(toDateObj);
      }
      else {
        const parkingFromSwedish = this.toSwedishISOString(this.data.parkingFrom);
        const todaySwedish = this.toSwedishISOString(today);

        if (parkingFromSwedish < todaySwedish) {
          fromDate = todaySwedish;
        } else {
          if (this.data.originalParkingFrom && typeof this.data.originalParkingFrom === 'string') {
            const fromDateForDST = new Date(this.data.originalParkingFrom.split('T')[0] + 'T12:00:00');
            fromDate = this.data.originalParkingFrom.replace(/\.\d+/, '.00') + this.getSwedishTimezoneOffset(fromDateForDST);
          } else {
            fromDate = this.toSwedishISOString(this.data.parkingTill);
          }
        }

        // Use original string values if available to preserve time
        if (this.data.originalParkingTill && typeof this.data.originalParkingTill === 'string') {
          const toDateForDST = new Date(this.data.originalParkingTill.split('T')[0] + 'T12:00:00');
          toDate = this.data.originalParkingTill.replace(/\.\d+/, '.00') + this.getSwedishTimezoneOffset(toDateForDST);
        } else {
          toDate = this.toSwedishISOString(this.data.parkingTill);
        }
      }

      let orgId = this.data.organization.organizationId?.toLowerCase();
      if (
        orgId === '3868ef69-054c-40bd-93ae-85cf08e4c71f' || orgId === '2868ef69-054c-40bd-93ae-85cf08e4c71f'
      ) {
        orgId = 'a848ccd3-fd8d-4035-925e-571ebb4e93e8';
      }

      const timeslots = await this.backofficeService
        .getAdditionalServicesTimeslots(orgId, fromDate, toDate)
        .toPromise();
      // For order details page, use the serviceTemplateId passed from parent
      const serviceId = this.data.serviceTemplateId ||
        this.data.additionalService.additionalServiceId ||
        this.data.additionalService.serviceId;

      if (serviceId) {
        const serviceSlots = timeslots.find(s => s.serviceId === serviceId);
        this.timeslots = serviceSlots?.slots.filter(slot => slot.capacity > (slot.busy || 0)) || [];
        this.availableDates = [...new Set(this.timeslots.map(slot => slot.localTime.split(' ')[0]))];
      } else {
        this.timeslots = [];
        this.availableDates = [];
      }
      if (this.selectedDate) {
        this.loadTimeSlotsForDate(this.selectedDate);
      }
    } catch (error) {
      console.error('Error loading timeslots:', error);
    }
  }

  dateFilter = (date: any): boolean => {
    if (!date || !this.availableDates.length) return true;

    // Handle Moment.js objects
    let dateObj: Date;
    if (date._isAMomentObject) {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return this.availableDates.includes(dateStr);
  }

  onDateChange(event: any): void {
    this.selectedDate = event.value;
    this.showTimeSlotDropdown = false;
    this.loadTimeSlotsForDate(this.selectedDate);
    this.editAdditionalServiceForm.get('plannedDate')?.setValue(null);
  }

  loadTimeSlotsForDate(date: any): void {
    if (!date || !this.timeslots.length) {
      this.availableTimeSlots = [];
      return;
    }

    const d = date instanceof Date ? date : new Date(date);
    const localDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    const localDateStr = localDate.toISOString().split('T')[0];

    this.availableTimeSlots = this.timeslots.filter(slot => {
      const slotDate = slot.localTime.split(' ')[0];
      return slotDate === localDateStr;
    });

    // Try to prefill if planned time matches an available slot
    if (this.data.additionalService.planned && this.availableTimeSlots.length > 0) {
      const plannedDate = new Date(this.data.additionalService.planned);
      const timezone = this.availableTimeSlots[0].localTime.split(' ')[2];
      const plannedLocalTime = `${plannedDate.getFullYear()}-${String(plannedDate.getMonth() + 1).padStart(2, '0')}-${String(plannedDate.getDate()).padStart(2, '0')} ${String(plannedDate.getHours()).padStart(2, '0')}:${String(plannedDate.getMinutes()).padStart(2, '0')} ${timezone}`;

      setTimeout(() => {
        this.selectedTimeSlot = this.availableTimeSlots.find(slot => slot.localTime === plannedLocalTime) || null;
      }, 0);
    }
  }

  initEmptyAdditionalServiceForm() {
    this.editAdditionalServiceForm = this.fb.group({
      orderParkingId: [''],
      status: ['New', Validators.required],
      plannedDate: [''],
      serviceProvider: [''],
      extraCharge: [''],
      comments: [''],
    });


    if (this.data.additionalService.status === 'Planned' || this.data.additionalService.status === 4) {
      this.editAdditionalServiceForm.get('plannedDate')?.setValidators(Validators.required);
    }
    this.editAdditionalServiceForm.get('status')?.valueChanges.subscribe((status) => {
      if (status === 'Planned' || status === 4) {
        this.editAdditionalServiceForm.get('plannedDate')?.setValidators(Validators.required);
      }
      else {
        this.editAdditionalServiceForm.get('plannedDate')?.clearValidators();
      }
      this.editAdditionalServiceForm.get('plannedDate')?.updateValueAndValidity();
    });

  }

  initEditAdditionalServiceForm() {
    this.editAdditionalServiceForm = this.fb.group({
      orderParkingId: [this.data.additionalService.orderParkingId],
      status: [this?.data?.status ? this.data.status : this.data.additionalService.status, Validators.required],
      plannedDate: [this.data.additionalService.planned],
      serviceProvider: [{ value: this.data.additionalService.serviceProvider, disabled: true }],
      extraCharge: [{ value: this.data.additionalService.extraCharge, disabled: true }],
      comments: [{ value: this.data.additionalService.comments, disabled: true }],
    });

    if (this.data.additionalService.status === 'Planned' || this.data.additionalService.status === 4) {
      this.editAdditionalServiceForm.get('plannedDate')?.setValidators(Validators.required);
    }
    this.editAdditionalServiceForm.get('status')?.valueChanges.subscribe((status) => {
      if (status === 'Planned' || status === 4) {
        this.editAdditionalServiceForm.get('plannedDate')?.setValidators(Validators.required);
      }
      else {
        this.editAdditionalServiceForm.get('plannedDate')?.clearValidators();
      }
      this.editAdditionalServiceForm.get('plannedDate')?.updateValueAndValidity();
    });
  }

  get filteredStatusOptions() {
    const currentStatus = this.data.additionalService.status;
    if (currentStatus === 'Paid' || currentStatus === 2 || currentStatus === 'Planned' || currentStatus === 4) {
      return this.statusOptions.filter(option => option.value !== 'New');
    }
    return this.statusOptions;
  }

  submitForm() {
    this.isLoading = true;

    const plannedValue = this.editAdditionalServiceForm.value.plannedDate;
    const model: EditOrderAdditionalServiceRequest = {
      id: this.data.additionalService.id,
      status: this.additionalServiceStatusValues[this.editAdditionalServiceForm.value.status],
      planned: plannedValue,
    };

    this.backofficeService.editOrderAdditionalServices(model, this.data.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.dialogRef.close();
          this.saveChangesEvent.emit();
        },
        error: (err) => {
          this.isLoading = false;
          this.snackBar.open(err.message, 'Error', {
            verticalPosition: 'top',
            duration: 2000
          });
        }
      });
  }

  onTimeSlotChange(slot: any): void {
    if (slot && slot.localTime) {
      this.editAdditionalServiceForm.get('plannedDate')?.setValue(slot.localTime);
      this.showTimeSlotDropdown = false;
    }
  }

  editTimeSlot(): void {
    this.showTimeSlotDropdown = true;
  }

  getDisplayTime(): string {
    const planned = this.data.additionalService.planned;
    if (planned) {
      const date = new Date(planned);
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
    return '';
  }

  setSwedishTimeZone(controlName: string, event: any) {
    const selectedDate = new Date(event.value);
    if (selectedDate) {
      const swedishOffset = -new Date().getTimezoneOffset();
      const swedishDate = new Date(selectedDate.getTime() + swedishOffset * 60 * 1000);
      this.editAdditionalServiceForm.get(controlName)?.setValue(swedishDate.toISOString().replace('Z', ''));
    } else {
      this.editAdditionalServiceForm.get(controlName)?.setValue(null);
    }
  }

  formatTimeSlot(slot: any): string {
    const time = slot.localTime.split(' ')[1];
    return `${time} (Capacity: ${slot.capacity})`;
  }

  clearDate(event) {
    event.stopPropagation();
    this.selectedDate = null;
    this.availableTimeSlots = [];
    this.selectedTimeSlot = null;
    this.showTimeSlotDropdown = false;
    this.editAdditionalServiceForm.get('plannedDate')?.setValue(null);
  }

  cancelDialog(): void {
    this.dialogRef.close();
  }
}
